<template>
	<div class="details-container display-flex flex-row flex-align-center flex-justify-space-between px-5">
		<Col class="width-100 mr-4">
			<div class="display-flex flex-row flex-align-center">
				<div class="card-container flex-centerize" v-if="icon">
					<SIcon :name="icon" width="20" />
				</div>
				<slot name="name-input">
					<StepNameInput class="ml-2" :hideActionText="hideActionText" />
				</slot>
			</div>
		</Col>
		<Col>
			<Row class="flex-align-center">
				<LockedStepDataProvider
					show-menu
					:serviceId="editNode.service"
					:key="editNodeCredentialsConfiguration.credentials"
					@showing-locked-indication="onShowingLockedIndication"
					:profileId="editNodeCredentialsConfiguration.credentials"
					:credentialsType="editNodeCredentialsConfiguration.credentials_type"
				/>
				<div class="ml-2">
					<AppTooltip text="Autofill with AI" v-if="showAutoFillButton">
						<IconButton
							@click="autofill"
							:disabled="isAutofillLoading"
							class="btn btn-sm autofill-btn"
							:class="{ loading: isAutofillLoading }"
						>
							<SparklesIcon />
						</IconButton>
					</AppTooltip>
				</div>
				<ActionUpgradeRequiredMenu :workflow="editWorkflow" :action="action" v-if="showUpgradeRequiredIcon" class="ml-2" />
				<ActionCreditsRequiredMenu :action="action" v-if="showCreditsIcon" class="mr-2" />
				<StepDropdownMenu
					v-if="showDropdownMenu"
					v-on="$listeners"
					:showDeleteOnly="showDeleteOnly"
					class="ml-2 display-flex flex-row"
					:isContextualStep="isContextualNode"
					:isStepLockedForMe="isStepLockedForMe"
					:hideProfileSelectionOption="hideProfileSelectionOption"
				/> </Row
		></Col>
	</div>
</template>

<script>
import StepNameInput from './StepDetails/StepNameInput.vue';
import StepDropdownMenu from './StepDropdownMenu.vue';
import ActionCreditsRequiredMenu from '@/components/BlockEditor/IntegrationEditor/ActionCreditsRequiredMenu.vue';
import ActionUpgradeRequiredMenu from '@/components/BlockEditor/IntegrationEditor/ActionUpgradeRequiredMenu.vue';
import LockedStepDataProvider from '@/components/BlockEditor/LockedStep/LockedStepDataProvider.vue';
import SparklesIcon from '@/components/common/RichTextBox/assets/sparkles.svg';
import { getAction } from '@/store/modules/actions/getters';
import { isAiAutofillEnabled } from '@/store/modules/featureFlags/getters';
import { handleAutofill } from '@/store/modules/selectedNode/actions';
import { editNode, flatPreviousSteps, isAutofillLoading } from '@/store/modules/selectedNode/getters';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import AppActionService from '@/utils/AppActionService';
import * as WorkflowNodeService from '@/utils/workflow-node-service';

export default {
	name: 'StepDetailsHeader',
	components: {
		StepNameInput,
		StepDropdownMenu,
		LockedStepDataProvider,
		SparklesIcon,
		ActionUpgradeRequiredMenu,
		ActionCreditsRequiredMenu,
	},
	props: {
		hideProfileSelectionOption: { type: Boolean },
		icon: { type: String },
		showDropdownMenu: { type: Boolean, default: true },
		isContextualNode: { type: Boolean },
		hideActionText: { type: Boolean, default: false },
	},
	computed: {
		...mapGettersShorthand({ editNode, flatPreviousSteps, isAiAutofillEnabled, isAutofillLoading, editWorkflow, getAction }),
		editNodeCredentialsConfiguration() {
			return this.editNode?.credentials_configuration || {};
		},
		showDeleteOnly() {
			return WorkflowNodeService.isLoop(this.editNode);
		},
		showAutoFillButton() {
			return this.isAiAutofillEnabled && this.editNodeCredentialsConfiguration.credentials;
		},
		showUpgradeRequiredIcon() {
			return AppActionService.isActionRestricted(this.action) || AppActionService.isActionOfSubFlow(this.action);
		},
		showCreditsIcon() {
			return AppActionService.isActionCreditBased(this.action);
		},
		action() {
			return this.getAction(this.editNode.action);
		},
	},
	methods: {
		...mapActionsShorthand({ handleAutofill }),
		onShowingLockedIndication(payload) {
			if (payload.isShowing && !payload.isLockedByMe) {
				this.isStepLockedForMe = true;
			} else {
				this.isStepLockedForMe = false;
			}
		},
		autofill() {
			this.handleAutofill({ flatPreviousSteps: this.flatPreviousSteps });
		},
	},
	data() {
		return {
			isStepLockedForMe: false,
		};
	},
};
</script>

<style scoped lang="scss">
.details-container {
	width: 100%;
	height: var(--block-editor-top-header);
	background-color: var(--header-background);
	border-bottom: 1px solid var(--border-color);

	.card-container {
		height: 48px;
		min-width: 48px;
		border-radius: 10px;
		background: var(--bg-color);
		border: 1px solid var(--border-color);
	}
}

@keyframes spin {
	100% {
		transform: rotate(360deg);
	}
}
.autofill-btn {
	height: 20px !important;
	width: 20px;
	&.loading {
		animation: spin 1s linear infinite;
	}
}
</style>
